/**
 * Upcoming / Past scheduling helpers shared by the delegate Manage Draws and
 * Live Scoring screens. Both list rodeos + jackpots and split them into an
 * "Upcoming" tab (events still ahead, soonest first) and a "Past" tab
 * (completed events, most recent first). Keeping the finished-check and the
 * sort comparator here means the two screens can never drift out of sync.
 *
 * The finished-check delegates to `isScheduleItemPast` in `@/utils/timezone`
 * so these delegate lists, the public /rodeos list, and the /jackpots list
 * all classify upcoming/past the exact same way: against "today" in the
 * rodeo's OWN timezone, not the browser's. A rodeo running tonight stays
 * upcoming through 11:59pm local and only flips to past at midnight there.
 */
import { isScheduleItemPast } from '@/utils/timezone';

export type ScheduleTab = 'upcoming' | 'past';

// Parse a Postgres `date` column ("YYYY-MM-DD") as a *local* midnight Date.
// `new Date("2026-05-25")` parses as UTC and can drift a day across
// timezones — building the Date from explicit parts keeps the sort
// comparator below ordering by the intended calendar day.
export function parseScheduleDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value) return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// A rodeo/jackpot is "past" once the day it ends has elapsed in the rodeo's
// own timezone. A multi-day event still in progress (end_date today or later)
// stays "upcoming". end_date drives past-ness; start_date is the fallback when
// there's no end_date. Undated rows are treated as upcoming so in-setup drafts
// stay visible to the delegate rather than disappearing into the Past tab.
export function isPastSchedule(item: any, now?: Date): boolean {
  return isScheduleItemPast({
    startDate: item?.start_date,
    endDate: item?.end_date,
    timezone: item?.timezone,
    now,
  });
}

// Sort items for display: the Upcoming tab puts the soonest event first
// (closest to today), the Past tab puts the most recent past event first.
// Undated rows always sort last regardless of tab.
export function sortScheduleByTab<T>(items: T[], tab: ScheduleTab): T[] {
  return [...items]
    .map((item) => ({
      item,
      date: parseScheduleDate((item as any)?.start_date) ?? parseScheduleDate((item as any)?.end_date),
    }))
    .sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return tab === 'upcoming'
        ? a.date.getTime() - b.date.getTime()
        : b.date.getTime() - a.date.getTime();
    })
    .map((x) => x.item);
}
